// src/pages/UpcomingProductsAdmin.jsx
import React, { useEffect, useState } from "react";
import { Card, Table, Button, Space, Switch, message, Popconfirm, Image, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined } from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import AddUpcomingProductForm from "../components/AddUpcomingProductForm";

const UpcomingProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get("/admin/upcoming-products");
      if (data && data.success && Array.isArray(data.products)) {
        setProducts(
          data.products.map((p) => ({
            ...p,
            // normalize possible variations in backend property names
            image_url: p.image_url ?? p.imageUrl ?? null,
            collection: p.collection ?? null,
            // default to New Arrivals now
            status: p.status ?? "New Arrivals",
            is_active: p.is_active === 1 || p.is_active === true,
          }))
        );
      } else {
        message.error("Unexpected response from server");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to load upcoming products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setOpenForm(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setOpenForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/admin/upcoming-products/${id}`);
      message.success("Deleted successfully");
      fetchProducts();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete");
    }
  };

  const toggleActive = async (id) => {
    try {
      await axiosInstance.patch(`/admin/upcoming-products/${id}/toggle`);
      message.success("Status updated");
      fetchProducts();
    } catch (err) {
      console.error(err);
      message.error("Failed to toggle status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New Arrivals": return "blue";
      case "Coming Soon": return "blue"; // fallback for existing legacy values
      case "Pre-Order": return "purple";
      case "Launching": return "green";
      default: return "gray";
    }
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image_url",
      width: 100,
      render: (val) =>
        val ? (
          <Image
            src={val}
            width={80}
            height={60}
            style={{
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
            }}
            preview={false}
          />
        ) : (
          <div style={{
            width: 80,
            height: 60,
            background: "#f3f4f6",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af"
          }}>
            No Image
          </div>
        ),
    },
    {
      title: "Product",
      dataIndex: "name",
      render: (name, record) => (
        <div>
          <div className="font-semibold text-gray-900">{name}</div>
          <div className="text-xs text-gray-500">{record.collection || "No collection"}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)} className="font-medium">
          {status}
        </Tag>
      ),
    },
    {
      title: "Active",
      dataIndex: "is_active",
      width: 100,
      render: (v, r) => (
        <Switch
          checked={r.is_active === 1 || r.is_active === true}
          onChange={() => toggleActive(r.id)}
          className={`${r.is_active ? 'bg-blue-600' : ''}`}
        />
      ),
    },
    {
      title: "Actions",
      width: 140,
      render: (_, r) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEdit(r)}
            className="text-blue-600 hover:text-blue-800"
          />
          <Popconfirm
            title="Delete this upcoming product?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(r.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              className="hover:text-red-700"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <ClockCircleOutlined className="text-blue-600" />
                Upcoming Products
              </h1>
              <p className="text-gray-600">
                Manage upcoming launches
              </p>
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              className="bg-blue-600 hover:bg-blue-700 border-blue-600 h-10 px-6 font-medium"
              size="large"
            >
              Add Product
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <Table
            rowKey="id"
            dataSource={products}
            columns={columns}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} products`
            }}
            className="ant-table-striped"
            rowClassName={(_, index) => index % 2 === 0 ? 'bg-gray-50/50' : ''}
          />
        </div>
      </div>

      <AddUpcomingProductForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditing(null);
        }}
        onSuccess={() => {
          setOpenForm(false);
          setEditing(null);
          fetchProducts();
        }}
        initialValues={editing}
      />
    </div>
  );
};

export default UpcomingProductsAdmin;
